import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectStoreConfig } from "../redux/Slice/siteSlice";
import { getBlogPost, getBlogPosts } from "../services/storefront";
import { errorMessage } from "../services";
import Loader from "./Loader";
import Icon from "../utils/icons";

// The store's "About us" page and blog (/about, /blog, /blog/<post>), written by the owner in the admin panel's website
// editor. Their text is plain: a blank line starts a new paragraph.
const paragraphs = (text) => String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
const postDate = (value) => (value ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "");
const Text = ({ text }) => paragraphs(text).map((p, i) => <p key={i}>{p}</p>);

// Shows `title` in the browser tab while the page is open, and puts the store's own title back when it closes
function keepTitle(title) {
  const before = document.title;
  document.title = title;
  return () => { document.title = before; };
}

export function AboutPage() {
    const config = useSelector(selectStoreConfig);
    const about = config.aboutPage || { title: "About us", text: config.about };
    useEffect(() => keepTitle(`${about.title} | ${config.name}`), [about.title, config.name]);
    return (
        <section className="storePage">
            <span className="storePageEyebrow">{config.name.toUpperCase()}</span>
            <h1>{about.title}</h1>
            {about.image && <img className="storePageCover" src={about.image} alt="" />}
            <div className="storePageText">{about.text ? <Text text={about.text} /> : <p>Welcome to {config.name}.</p>}</div>
        </section>
    );
}

export function BlogPage() {
    const config = useSelector(selectStoreConfig);
    const location = useLocation();
    const navigate = useNavigate();
    const slug = decodeURIComponent((location.pathname.match(/^\/blog\/([^/]+)/) || [])[1] || "");
    const [state, setState] = useState({ data: null, error: "" });

    useEffect(() => {
        let live = true;
        setState({ data: null, error: "" });
        (slug ? getBlogPost(slug) : getBlogPosts())
            .then((data) => live && setState({ data, error: "" }))
            .catch((err) => live && setState({ data: null, error: err.response && err.response.status === 404 ? "This post could not be found." : errorMessage(err) }));
        return () => { live = false; };
    }, [slug]);
    useEffect(() => keepTitle(`${slug && state.data ? state.data.title : "Blog"} | ${config.name}`), [slug, state.data, config.name]);

    const open = (to) => {
        navigate(to);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (state.error) return <section className="storePage"><div className="noResults">{state.error} <button className="textBtn" onClick={() => open("/blog")}>See all posts</button></div></section>;
    if (!state.data) return <Loader label="Loading" />;

    if (slug) {
        const post = state.data;
        return (
            <article className="storePage">
                <button className="storePageBack" onClick={() => open("/blog")}>← All posts</button>
                <h1>{post.title}</h1>
                <small className="storePageDate">{postDate(post.publishedAt)}</small>
                {post.coverImage && <img className="storePageCover" src={post.coverImage} alt="" />}
                <div className="storePageText"><Text text={post.body} /></div>
            </article>
        );
    }

    return (
        <section className="storePage storePageWide">
            <span className="storePageEyebrow">{config.name.toUpperCase()}</span>
            <h1>Blog</h1>
            {!state.data.length && <div className="noResults">No posts yet.</div>}
            <div className="storeBlogList">
                {state.data.map((post) => (
                    <button className="storeBlogCard" key={post.slug} onClick={() => open(`/blog/${post.slug}`)}>
                        {post.coverImage ? <img src={post.coverImage} alt="" loading="lazy" /> : <span className="storeBlogPlaceholder" aria-hidden="true">✎</span>}
                        <span className="storeBlogCardText">
                            <small>{postDate(post.publishedAt)}</small>
                            <b>{post.title}</b>
                            <span>{post.excerpt}</span>
                        </span>
                    </button>
                ))}
            </div>
        </section>
    );
}

// "Contact us" (/contact): WhatsApp, call and email the shop, and its address. The details are the owner's own
// (Store settings) or, until they add them, the number and email they registered with (backend services/storeContact.js).
const waLink = (number, storeName) => `https://wa.me/${number}?text=${encodeURIComponent(`Hello ${storeName}, I have a question.`)}`;

export function ContactPage() {
    const config = useSelector(selectStoreConfig);
    useEffect(() => keepTitle(`Contact us | ${config.name}`), [config.name]);
    const ways = [
        config.whatsapp && { icon: <Icon.Whatsapp />, label: "WhatsApp", value: "Chat with us", href: waLink(config.whatsapp, config.name), external: true, main: true },
        config.contact && { icon: <Icon.TelephoneFill />, label: "Call us", value: config.contact, href: `tel:${String(config.contact).replace(/[^\d+]/g, "")}` },
        config.email && { icon: <Icon.EnvelopeFill />, label: "Email", value: config.email, href: `mailto:${config.email}` },
    ].filter(Boolean);
    return (
        <section className="storePage contactPage">
            <span className="storePageEyebrow">{config.name.toUpperCase()}</span>
            <h1>Contact us</h1>
            <p className="contactIntro">Questions about a product or your order? We are happy to help. Message us on WhatsApp for the quickest reply.</p>
            {ways.length ? (
                <div className="contactWays">
                    {ways.map((way) => (
                        <a key={way.label} className={`contactWay ${way.main ? "is-main" : ""}`} href={way.href} {...(way.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                            <span className="contactWayIcon" aria-hidden="true">{way.icon}</span>
                            <span><small>{way.label}</small><b>{way.value}</b></span>
                        </a>
                    ))}
                </div>
            ) : (
                <p>Contact details will be added soon.</p>
            )}
            {config.address && (
                <div className="contactAddress">
                    <small>Address</small>
                    <p>{config.address}</p>
                </div>
            )}
        </section>
    );
}

// The floating WhatsApp button on every page: a chat with the shop, with a message ready to send
export function FloatingWhatsApp({ raised = false }) {
    const config = useSelector(selectStoreConfig);
    if (!config.whatsapp) return null;
    return (
        <a className={`floatingWhatsApp ${raised ? "is-raised" : ""}`} href={waLink(config.whatsapp, config.name)} target="_blank" rel="noopener noreferrer" aria-label={`Chat with ${config.name} on WhatsApp`} title="Chat on WhatsApp">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.4.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1 2.7c.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.6-.3Z" /></svg>
        </a>
    );
}
